// What an export sends for the ticked clips, in pick order — what the worker cuts.
//
// - A real clip rides on its pk (list_of_ids); its ✏ edit in edited_text by pk.
// - A SPLIT piece ("456-a", "456-b-a" …) has no row of its own: its parent's pk repeats once
//   per piece, and clip_times (aligned with list_of_ids) carries the piece's own times and
//   words. (Writing every piece into adjusted_times[parent] made each overwrite the one before
//   — only the LAST piece came out.)
// - A re-segmented span (Segment by → By seconds "iv-3" / Word by word "w-12") has no row
//   either, and several can come from one sentence, so it goes as an explicit segment.

/** A re-segmented span's id — no database row behind it. */
export const isSyntheticId = (id) => typeof id === 'string' && (id.startsWith('iv-') || id.startsWith('w-'));

/**
 * @param {Array} selectedIds  the ticked ids, in pick order
 * @param {Function} rowOf     id → the clip's row (or undefined)
 * @param {Object} opts        edits: {id: text} (the ET's ✏), adjustedTimes: {id: {start_in, end_in}}
 *                             (pen highlights / ± trims, by the id the user sees)
 */
export const buildExportClips = (selectedIds, rowOf, { edits = {}, adjustedTimes = {} } = {}) => {
  const exportIds = [];
  const clipTimes = [];
  const segments = [];
  const editedText = {};
  (selectedIds || []).forEach((id) => {
    const row = rowOf(id);
    if (!row) return;
    const edit = edits[id];
    if (isSyntheticId(id)) {
      segments.push({ start_in: row.start_in, end_in: row.end_in, text: edit ?? row.sentence ?? '' });
    } else if (row._splitId !== undefined) {
      const t = adjustedTimes[id] || row;
      exportIds.push(row._splitId);
      clipTimes.push({ start_in: Number(t.start_in), end_in: Number(t.end_in), text: edit ?? row.sentence ?? '' });
    } else {
      if (edit !== undefined) editedText[id] = edit;
      exportIds.push(id);
      clipTimes.push(null);
    }
  });
  // Cut re-segmented clips in timeline order so the stitched video flows.
  segments.sort((a, b) => (a.start_in || 0) - (b.start_in || 0));
  return { exportIds, clipTimes, segments, editedText };
};
